import { Injectable, inject } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import {
  Action,
  NgxsAfterBootstrap,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { CompanySettingsService } from '@wilson/company-settings/core';
import {
  MobileSetting,
  MobileSettingColleagueShiftVisibilityEnum,
  MobileSettingMinBreakTimeInMinutesViolation,
} from '@wilson/interfaces';
import {
  BehaviorSubject,
  catchError,
  finalize,
  Observable,
  of,
  tap,
} from 'rxjs';
import { InitializeMobileCompanySettingsState } from './mobile-company-settings.action';

export const MOBILE_COMPANY_SETTINGS_NAME = 'mobileCompanySettings';

@State<MobileSetting>({
  name: MOBILE_COMPANY_SETTINGS_NAME,
  defaults: {
    absenceCategoryIds: [],
    allowAbsenceCreation: false,
    allowAutomaticInterruptionActivity: false,
    allowSpacesBetweenActivities: false,
    autoShiftReporting: false,
    checkBreakRegulations: false,
    mobileSettingAbsenceCategories: [],
    organizationalUnitId: '',
    requireRestPeriodBetweenShifts: false,
    requireSickLeaveAttachment: false,
    shiftStartBeforePlannedInMinutes: 0,
    minBreakTimeInMinutesViolation: null,
    allowUsersToWithdrawShiftSubmissions: false,
    canDeclineShift: null,
    requireDeclineReasons: null,
    shiftDeclineReasons: [],
    trackShiftLastSeen: false,
    canConfirmShift: false,
    colleagueShiftVisibility: MobileSettingColleagueShiftVisibilityEnum.None,
    requireAgreementForShiftSubmission: false,
    showHomeWidgetTimeWorkedMonthlyTarget: false,
    showHomeWidgetBalanceOvertimeAccount: false,
    showDashboard: false,
    showDashboardWorkingTime: false,
    showDashboardOvertimeAndOtherAccounts: false,
    showDashboardPayPremiums: false,
    showDashboardOther: false,
    showDashboardTimesheet: false,
  },
})
@Injectable()
export class MobileCompanySettingsState implements NgxsAfterBootstrap {
  private readonly companySettingsService = inject(CompanySettingsService);
  private readonly storage = inject(Storage);
  private readonly store = inject(Store);

  public syncingMobileSetting$ = new BehaviorSubject<boolean>(false);

  @Selector()
  static mobileCompanySettingsState(state: MobileSetting): MobileSetting {
    return state;
  }

  @Selector()
  static allowAbsenceCreation(state: MobileSetting): boolean {
    return state.allowAbsenceCreation;
  }
  @Selector()
  static allowUsersToWithdrawShiftSubmissions(state: MobileSetting): boolean {
    return state.allowUsersToWithdrawShiftSubmissions;
  }

  @Selector()
  static minBreakTimeInMinutesViolation(
    state: MobileSetting,
  ): MobileSettingMinBreakTimeInMinutesViolation | null {
    return state.minBreakTimeInMinutesViolation;
  }

  @Selector()
  static declineReasons(state: MobileSetting): string[] {
    return state.shiftDeclineReasons;
  }

  @Selector()
  static trackShiftLastSeen(state: MobileSetting): boolean {
    return state.trackShiftLastSeen;
  }

  @Selector()
  static canConfirmShift(state: MobileSetting): boolean {
    return state.canConfirmShift;
  }

  @Selector()
  static requireAgreementForShiftSubmission(state: MobileSetting): boolean {
    return state.requireAgreementForShiftSubmission;
  }

  @Selector()
  static colleagueShiftVisibility(
    state: MobileSetting,
  ): MobileSettingColleagueShiftVisibilityEnum {
    return state.colleagueShiftVisibility;
  }

  @Selector()
  static showHomeWidgetTimeWorkedMonthlyTarget(state: MobileSetting): boolean {
    return state.showHomeWidgetTimeWorkedMonthlyTarget;
  }

  @Selector()
  static showHomeWidgetBalanceOvertimeAccount(state: MobileSetting): boolean {
    return state.showHomeWidgetBalanceOvertimeAccount;
  }

  @Selector()
  static showDashboard(state: MobileSetting): boolean {
    return state.showDashboard;
  }

  @Selector()
  static showDashboardWorkingTime(state: MobileSetting): boolean {
    return state.showDashboardWorkingTime;
  }

  @Selector()
  static showDashboardOvertimeAndOtherAccounts(state: MobileSetting): boolean {
    return state.showDashboardOvertimeAndOtherAccounts;
  }

  @Selector()
  static showDashboardPayPremiums(state: MobileSetting): boolean {
    return state.showDashboardPayPremiums;
  }

  @Selector()
  static showDashboardOther(state: MobileSetting): boolean {
    return state.showDashboardOther;
  }

  @Selector()
  static showDashboardTimesheet(state: MobileSetting): boolean {
    return state.showDashboardTimesheet;
  }

  async ngxsAfterBootstrap(
    context: StateContext<MobileSetting>,
  ): Promise<void> {
    const mobileSetting = await this.storage.get(MOBILE_COMPANY_SETTINGS_NAME);
    if (mobileSetting) context.patchState(mobileSetting);
  }

  @Action(InitializeMobileCompanySettingsState)
  initializeMobileCompanySettingsState(
    context: StateContext<MobileSetting>,
  ): Observable<MobileSetting[] | null> {
    const user = this.store.selectSnapshot(AuthState.user);

    if (!user) return of(null);

    this.syncingMobileSetting$.next(true);

    return this.companySettingsService
      .getMobileSettings({
        relations: ['mobileSettingAbsenceCategories'],
      })
      .pipe(
        tap(([mobileSetting]) => {
          context.patchState(mobileSetting);
          this.storage.set(MOBILE_COMPANY_SETTINGS_NAME, mobileSetting);
        }),
        finalize(() => {
          this.syncingMobileSetting$.next(false);
        }),
        catchError(() => {
          return of(null);
        }),
      );
  }
}
